import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { UpfrontPaymentButtonRow } from "./UpfrontPayment.styles";
import { Button } from "../shared/Button/Button";
import { StyledButton } from "../shared/Button/Button.styles";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { SavePaymentMethodFromSetupIntent } from "./Services/UpfrontPaymentService";
import { getUserAuth } from "../../recoil/selectors";
import { ErrorMessage } from "./EditPaymentMethodModal.styles";
import { TrackAnalyticsEvent } from "../../services/Analytics";

export interface PaymentMethodInfo {
  payment_method_id: string;
  card_brand: string;
  last_four_digits: string;
}

interface EditPaymentMethodFormProps {
  clientSecret: string;
  onClose: () => void;
  onSuccess: (paymentMethodInfo: PaymentMethodInfo) => void;
  reqIdentifier: string;
}

export const EditPaymentMethodForm: React.FC<EditPaymentMethodFormProps> = ({
  clientSecret,
  onClose,
  onSuccess,
  reqIdentifier,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { billingPK } = useParams<{ billingPK: string }>();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [stripeReady, setStripeReady] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !billingPK || !clientSecret) {
      return;
    }

    setLoading(true);
    setError(undefined);

    try {
      // Submit elements form first (required by Stripe)
      const elementsResult = await elements.submit();
      if (elementsResult.error) {
        setError(
          elementsResult.error.message ||
            t("upfrontPayment.editPaymentMethod.checkPaymentDetails"),
        );
        return;
      }

      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (submitError) {
        setError(submitError.message || "Failed to update payment method");
        return;
      }

      if (!setupIntent || setupIntent.status !== "succeeded") {
        setError("Failed to confirm payment details");
        return;
      }

      const confirmResponse = await SavePaymentMethodFromSetupIntent(
        billingPK,
        setupIntent.id,
      );

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json();
        setError(errorData.error || "Failed to save payment method");
        return;
      }

      const confirmData = await confirmResponse.json();

      // Validate that all required fields are present in the API response
      if (!confirmData.payment_method_id) {
        setError("Invalid response: payment method ID is missing");
        return;
      }

      if (!confirmData.card_brand) {
        setError("Invalid response: card brand information is missing");
        return;
      }

      if (!confirmData.last_four_digits) {
        setError("Invalid response: card number information is missing");
        return;
      }

      const paymentMethodInfo = {
        payment_method_id: confirmData.payment_method_id,
        card_brand: confirmData.card_brand,
        last_four_digits: confirmData.last_four_digits,
      };

      // Track successful payment method update
      TrackAnalyticsEvent("interaction", undefined, reqIdentifier, {
        interaction_type: "form_submit",
        button_text: "Save",
        element_name: "save_payment_method_form",
        page_name: "/client/upfront-payment",
      });

      onSuccess(paymentMethodInfo);
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: "24px" }}>
        {!stripeReady && <SpinnerLoading />}
        <PaymentElement
          options={{
            paymentMethodOrder: ["card"],
            layout: {
              type: "tabs",
            },
          }}
          onReady={() => setStripeReady(true)}
        />
      </div>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <UpfrontPaymentButtonRow>
        <StyledButton
          type="submit"
          size="medium"
          $backgroundColor="greenDark"
          disabled={!stripe || !clientSecret || loading}
        >
          {loading
            ? t("upfrontPayment.editPaymentMethod.saving")
            : t("upfrontPayment.editPaymentMethod.save")}
        </StyledButton>
      </UpfrontPaymentButtonRow>
      <UpfrontPaymentButtonRow bottomRow tertiary>
        <Button
          label={t("upfrontPayment.actions.cancel")}
          size="medium"
          backgroundColor="greyMedium"
          onClick={onClose}
          reverse
        />
      </UpfrontPaymentButtonRow>
    </form>
  );
};
